import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Eye, MousePointerClick, FileCheck, Percent, TrendingUp, Clock, ArrowUp, ArrowDown } from 'lucide-react';

interface OverviewMetric {
  title: string;
  value: string;
  change: number;
  changeLabel: string;
  icon: React.ElementType;
}

interface OverviewCardsProps {
  totalViews?: number;
  formStarts?: number;
  responses?: number;
  completionRate?: number;
  conversionRate?: number;
  avgCompletionTime?: number;
  prevTotalViews?: number;
  prevFormStarts?: number;
  prevResponses?: number;
  prevCompletionRate?: number;
  prevConversionRate?: number;
  prevAvgCompletionTime?: number;
}

function calcChange(current: number, previous: number): number {
  const curr = Number.isFinite(current) ? current : 0;
  const prev = Number.isFinite(previous) ? previous : 0;
  if (prev === 0) return curr > 0 ? 100 : 0;
  return Math.round(((curr - prev) / prev) * 100);
}

function formatTime(seconds: number): string {
  const secs = Math.max(0, Number.isFinite(seconds) ? seconds : 0);
  if (secs === 0) return 'N/A';
  if (secs < 60) return `${secs}s`;
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

export function OverviewCards(props: OverviewCardsProps) {
  const totalViews = props.totalViews ?? 0;
  const formStarts = props.formStarts ?? 0;
  const responses = props.responses ?? 0;
  const completionRate = props.completionRate ?? 0;
  const conversionRate = props.conversionRate ?? 0;
  const avgCompletionTime = props.avgCompletionTime ?? 0;

  const prevTotalViews = props.prevTotalViews ?? 0;
  const prevFormStarts = props.prevFormStarts ?? 0;
  const prevResponses = props.prevResponses ?? 0;
  const prevCompletionRate = props.prevCompletionRate ?? 0;
  const prevConversionRate = props.prevConversionRate ?? 0;
  const prevAvgCompletionTime = props.prevAvgCompletionTime ?? 0;

  const metrics: OverviewMetric[] = [
    {
      title: 'Form Views',
      value: totalViews.toLocaleString(),
      change: calcChange(totalViews, prevTotalViews),
      changeLabel: 'vs previous period',
      icon: Eye,
    },
    {
      title: 'Form Starts',
      value: formStarts.toLocaleString(),
      change: calcChange(formStarts, prevFormStarts),
      changeLabel: 'vs previous period',
      icon: MousePointerClick,
    },
    {
      title: 'Responses',
      value: responses.toLocaleString(),
      change: calcChange(responses, prevResponses),
      changeLabel: 'vs previous period',
      icon: FileCheck,
    },
    {
      title: 'Completion Rate',
      value: `${completionRate}%`,
      change: calcChange(completionRate, prevCompletionRate),
      changeLabel: 'vs previous period',
      icon: Percent,
    },
    {
      title: 'Conversion Rate',
      value: `${conversionRate}%`,
      change: calcChange(conversionRate, prevConversionRate),
      changeLabel: 'views → responses',
      icon: TrendingUp,
    },
    {
      title: 'Avg. Time to Complete',
      value: formatTime(avgCompletionTime),
      change: calcChange(avgCompletionTime, prevAvgCompletionTime),
      changeLabel: 'vs previous period',
      icon: Clock,
    },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
      {metrics.map((m) => {
        const isPositive = m.change >= 0;
        const isGood = m.title === 'Avg. Time to Complete' ? !isPositive : isPositive;
        return (
          <Card key={m.title} className="relative overflow-hidden">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                {m.title}
              </CardTitle>
              <m.icon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-foreground">{m.value}</div>
              <div className="flex items-center gap-1 mt-1">
                {m.change !== 0 && (
                  <>
                    {isPositive ? (
                      <ArrowUp className={`h-3 w-3 ${isGood ? 'text-emerald-500' : 'text-red-500'}`} />
                    ) : (
                      <ArrowDown className={`h-3 w-3 ${isGood ? 'text-emerald-500' : 'text-red-500'}`} />
                    )}
                    <span className={`text-xs font-medium ${isGood ? 'text-emerald-500' : 'text-red-500'}`}>
                      {Math.abs(m.change)}%
                    </span>
                  </>
                )}
                <span className="text-xs text-muted-foreground">{m.changeLabel}</span>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
